// owned by WP-SEC-UI · the side sheet of the administration and Trust Centre screens (APPLE-SPEC §7 10.14: 560 px, white, a 0.5 px
// leading edge, header 52 with a 15 / 600 title, the footer in the solid material, primary last; Esc closes; focus trapped and returned).
import { useEffect, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { useTranslation } from "@ncp/i18n";
import { IconButton } from "../theme/primitives";
import "./admin.css";

export function Drawer({ title, sub, lead, onClose, footer, children, labelId, bare }: {
  title: ReactNode;
  sub?: ReactNode;
  lead?: ReactNode;
  onClose: () => void;
  footer?: ReactNode;
  children: ReactNode;
  labelId: string;
  /** The children bring their own body and footer (the account sheet). */
  bare?: boolean;
}) {
  const { t } = useTranslation();
  const panel = useRef<HTMLDivElement>(null);
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    panel.current?.querySelector<HTMLElement>(".ec-drawer-b button, .ec-drawer-b [href], .ec-drawer-b input, .ec-drawer-h button")?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close.current();
      if (e.key !== "Tab" || !panel.current) return;
      const items = [...panel.current.querySelectorAll<HTMLElement>("button:not([disabled]), [href], input:not([disabled]), select, textarea, [tabindex]:not([tabindex='-1'])")];
      if (!items.length) return;
      const first = items[0]!;
      const last = items[items.length - 1]!;
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      previous?.focus?.();
    };
  }, []);
  return createPortal(
    <>
      <div className="ec-scrim ec-adm-scrim" aria-hidden onClick={() => close.current()} />
      <div ref={panel} role="dialog" aria-modal="true" aria-labelledby={labelId} className="ec-drawer ec-adm-drawer">
        <div className="ec-drawer-h">
          {lead}
          <h2 id={labelId}>
            {title}
            {sub ? <span className="sub">{sub}</span> : null}
          </h2>
          <IconButton icon={<X aria-hidden />} label={t("assistant:econic.admin.drawer.close")} onClick={() => close.current()} />
        </div>
        {bare ? children : <div className="ec-drawer-b">{children}</div>}
        {footer ? <div className="ec-drawer-f ec-adm-dfoot">{footer}</div> : null}
      </div>
    </>,
    document.body,
  );
}
